import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { IUserModel, IRvcModelAvailable, MusicEditingType, CreateMusicEditingRequest, ModelType } from '@packages/global';
import { ModelSelector } from '../common/form';
import AudioUpload from '../common/AudioUpload';
import { useCoinStore } from '../../stores/coinStore';
import { rvcModelUserApi } from '../../services/user/rvcModel.api';

export interface MusicEditFormData {
  editingType: MusicEditingType;
  modelId: string;
  srcAudioUrl: string;
  // cover 专用
  referenceAudioUrl: string;
  audioCoverStrength: number;
  coverNoiseStrength: number;
  // repaint 专用
  repaintingStart: number | '';
  repaintingEnd: number | '';
  // song_cover 专用
  rvcModelId: string;
  pitch: number;
  indexRate: number;
  vocalGainDb: number;
  instrumentalGainDb: number;
  cleanAudio: boolean;
  f0Autotune: boolean;
  // separate 专用
  uvrModel: string;
  enableBackingVocalRemoval: boolean;
  outputFormat: string;
  // 旧字段保留兼容
  trackName: 'vocals' | 'accompaniment';
}

export const DEFAULT_EDIT_FORM: MusicEditFormData = {
  editingType: 'song_cover',
  modelId: '',
  srcAudioUrl: '',
  referenceAudioUrl: '',
  audioCoverStrength: 0.95,
  coverNoiseStrength: 0.35,
  repaintingStart: '',
  repaintingEnd: '',
  rvcModelId: '',
  pitch: 0,
  indexRate: 0.6,
  vocalGainDb: 0,
  instrumentalGainDb: 0,
  cleanAudio: false,
  f0Autotune: false,
  uvrModel: '',
  enableBackingVocalRemoval: true,
  outputFormat: '',
  trackName: 'vocals',
};

interface Props {
  formData: MusicEditFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onFormDataChange: (data: Partial<MusicEditFormData>) => void;
  onSubmit: (data: CreateMusicEditingRequest) => Promise<void>;
}

const EDITING_TYPES: MusicEditingType[] = ['song_cover', 'cover', 'repaint', 'separate'];

export default function MusicEditingForm({ formData, models, modelsLoading, onFormDataChange, onSubmit }: Props) {
  const { t } = useTranslation(['generation', 'common']);
  const { account } = useCoinStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rvcModels, setRvcModels] = useState<IRvcModelAvailable[]>([]);
  const [rvcModelsLoading, setRvcModelsLoading] = useState(false);

  const EDITING_TYPE_LABELS: Record<string, string> = {
    song_cover: t('generation:musicEdit.form.typeSongCover'),
    cover: t('generation:musicEdit.form.typeCover'),
    repaint: t('generation:musicEdit.form.typeRepaint'),
    separate: t('generation:musicEdit.form.typeSeparate'),
  };

  const OUTPUT_FORMAT_OPTIONS = [
    { value: '', label: t('generation:musicEdit.form.outputFormatDefault') },
    { value: 'MP3', label: 'MP3' },
    { value: 'WAV', label: 'WAV' },
  ];

  const musicEditModels = models.filter(m => m.modelType === ModelType.MUSIC_EDIT);
  const selectedModel = musicEditModels.find(m => m.id === formData.modelId);
  const modelPrice = selectedModel?.coinPrice ?? 0;
  const hasEnoughCoins = (account?.balance ?? 0) >= modelPrice;

  useEffect(() => {
    if (!modelsLoading && !formData.modelId && musicEditModels.length > 0) {
      onFormDataChange({ modelId: musicEditModels[0].id });
    }
  }, [musicEditModels, modelsLoading, formData.modelId]);

  // 切换到 song_cover 时加载可用 RVC 模型
  useEffect(() => {
    if (formData.editingType === 'song_cover' && rvcModels.length === 0) {
      setRvcModelsLoading(true);
      rvcModelUserApi.getAvailable()
        .then(list => {
          setRvcModels(list);
          // 自动选中第一个
          if (list.length > 0 && !formData.rvcModelId) {
            onFormDataChange({ rvcModelId: list[0].applioModelId });
          }
        })
        .catch(() => { })
        .finally(() => setRvcModelsLoading(false));
    }
  }, [formData.editingType]);

  const validate = (): string => {
    if (!formData.modelId) return t('generation:musicEdit.form.validationSelectModel');
    if (!formData.srcAudioUrl) return t('generation:musicEdit.form.validationUploadAudio');
    if (!hasEnoughCoins && modelPrice > 0) return t('generation:musicEdit.form.validationInsufficientCoins', { price: modelPrice });
    if (formData.editingType === 'cover' && !formData.referenceAudioUrl) return t('generation:musicEdit.form.validationUploadReference');
    if (formData.editingType === 'repaint') {
      if (formData.repaintingStart === '' || formData.repaintingEnd === '') return t('generation:musicEdit.form.validationRepaintTime');
      if (Number(formData.repaintingStart) >= Number(formData.repaintingEnd)) return t('generation:musicEdit.form.validationRepaintOrder');
    }
    if (formData.editingType === 'song_cover' && !formData.rvcModelId.trim()) return t('generation:musicEdit.form.validationSelectRvc');
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = validate();
    if (msg) { setError(msg); return; }
    setError('');
    setSubmitting(true);
    try {
      const data: CreateMusicEditingRequest = {
        editingType: formData.editingType,
        srcAudioUrl: formData.srcAudioUrl,
        modelId: formData.modelId,
        ...(formData.editingType === 'cover' && {
          referenceAudioUrl: formData.referenceAudioUrl,
          audioCoverStrength: formData.audioCoverStrength,
          coverNoiseStrength: formData.coverNoiseStrength,
        }),
        ...(formData.editingType === 'repaint' && {
          repaintingStart: Number(formData.repaintingStart),
          repaintingEnd: Number(formData.repaintingEnd),
        }),
        ...(formData.editingType === 'song_cover' && {
          rvcModelId: formData.rvcModelId,
          pitch: formData.pitch,
          indexRate: formData.indexRate,
          vocalGainDb: formData.vocalGainDb,
          instrumentalGainDb: formData.instrumentalGainDb,
          cleanAudio: formData.cleanAudio || undefined,
          f0Autotune: formData.f0Autotune || undefined,
          outputFormat: formData.outputFormat || undefined,
        }),
        ...(formData.editingType === 'separate' && {
          uvrModel: formData.uvrModel || undefined,
          enableBackingVocalRemoval: formData.enableBackingVocalRemoval,
          outputFormat: formData.outputFormat || undefined,
        }),
      };
      await onSubmit(data);
      onFormDataChange({ srcAudioUrl: '' });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !modelsLoading && !!formData.modelId && !!formData.srcAudioUrl && hasEnoughCoins;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* 编辑类型 Tab */}
        <div className="flex rounded-lg overflow-hidden border border-border-subtle bg-bg-elevated">
          {EDITING_TYPES.map(type => (
            <button
              key={type}
              type="button"
              onClick={() => { onFormDataChange({ editingType: type }); setError(''); }}
              className={`flex-1 py-1.5 text-xs font-medium transition-colors cursor-pointer ${formData.editingType === type
                ? 'bg-primary text-white'
                : 'text-gray-400 hover:text-gray-200'
                }`}
            >
              {EDITING_TYPE_LABELS[type]}
            </button>
          ))}
        </div>

        {/* 模型选择 */}
        <ModelSelector
          models={musicEditModels}
          selectedModelId={formData.modelId}
          onChange={(modelId) => onFormDataChange({ modelId })}
          loading={modelsLoading}
          placeholder={t('generation:musicEdit.form.selectModel')}
        />

        {/* 源音频上传 */}
        <div>
          <p className="text-xs text-gray-500 mb-1.5">{t('generation:musicEdit.form.sourceAudio')}</p>
          <AudioUpload
            value={formData.srcAudioUrl}
            onUploadSuccess={(url) => onFormDataChange({ srcAudioUrl: url })}
            showAudioSelector={false}
            showMusicSelector={true}
            placeholder={t('generation:musicEdit.form.uploadSourceAudio')}
          />
        </div>

        {/* cover 专用字段 */}
        {formData.editingType === 'cover' && (
          <>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">{t('generation:musicEdit.form.referenceAudio')}</p>
              <AudioUpload
                value={formData.referenceAudioUrl}
                onUploadSuccess={(url) => onFormDataChange({ referenceAudioUrl: url })}
                showAudioSelector={false}
                showMusicSelector={true}
                placeholder={t('generation:musicEdit.form.uploadReferenceAudio')}
              />
            </div>
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-300 font-medium">{t('generation:musicEdit.form.variationDegree')}</span>
                <span className="text-xs font-mono text-primary">
                  {(() => {
                    const noise = Math.max(0, Math.min(0.5, formData.coverNoiseStrength));
                    const v = 0.5 - noise;
                    if (v <= 0.1) return t('generation:musicEdit.form.variationMinor');
                    if (v <= 0.25) return t('generation:musicEdit.form.variationLight');
                    if (v <= 0.4) return t('generation:musicEdit.form.variationMedium');
                    return t('generation:musicEdit.form.variationLarge');
                  })()}
                </span>
              </div>
              <input
                type="range" min={0} max={0.5} step={0.025}
                value={0.5 - Math.max(0, Math.min(0.5, formData.coverNoiseStrength))}
                onChange={e => {
                  const uiVal = parseFloat(e.target.value);
                  onFormDataChange({ coverNoiseStrength: Math.max(0, Math.min(0.5, 0.5 - uiVal)) });
                }}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-[10px] text-gray-600">
                <span>{t('generation:musicEdit.form.variationNearOriginal')}</span>
                <span>{t('generation:musicEdit.form.variationFarOriginal')}</span>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {t('generation:musicEdit.form.variationDescription')}
              </p>
            </div>
          </>
        )}

        {/* repaint 专用字段 */}
        {formData.editingType === 'repaint' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('generation:musicEdit.form.repaintStart')}</label>
              <input
                type="number" min={0} step={0.1}
                value={formData.repaintingStart}
                onChange={e => onFormDataChange({ repaintingStart: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                placeholder="0.0"
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 placeholder-gray-600 focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('generation:musicEdit.form.repaintEnd')}</label>
              <input
                type="number" min={0} step={0.1}
                value={formData.repaintingEnd}
                onChange={e => onFormDataChange({ repaintingEnd: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                placeholder="10.0"
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 placeholder-gray-600 focus:outline-none focus:border-primary"
              />
            </div>
          </div>
        )}

        {/* song_cover 专用字段 */}
        {formData.editingType === 'song_cover' && (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('generation:musicEdit.form.rvcModelLabel')} <span className="text-red-400">*</span></label>
              {rvcModelsLoading ? (
                <div className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-500">
                  {t('generation:musicEdit.form.rvcModelLoading')}
                </div>
              ) : rvcModels.length === 0 ? (
                <div className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-500">
                  {t('generation:musicEdit.form.rvcModelEmpty')}
                </div>
              ) : (
                <select
                  value={formData.rvcModelId}
                  onChange={e => onFormDataChange({ rvcModelId: e.target.value })}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
                >
                  <option value="">{t('generation:musicEdit.form.rvcModelPlaceholder')}</option>
                  {rvcModels.map(m => (
                    <option key={m.id} value={m.applioModelId}>
                      {m.name}{m.description ? ` — ${m.description}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* 高级选项折叠 */}
            <div className="border border-border-subtle rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-gray-400 hover:text-gray-200 hover:bg-bg-elevated transition-colors cursor-pointer"
              >
                <span>{t('generation:musicEdit.form.advancedOptions')}</span>
                <span className="text-[10px]">{showAdvanced ? '▲' : '▼'}</span>
              </button>
              {showAdvanced && (
                <div className="px-4 pb-4 pt-1 space-y-3 bg-bg-surface">
                  {/* 音调偏移 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-300 font-medium">{t('generation:musicEdit.form.pitchLabel')}</span>
                      <span className="text-xs font-mono text-primary">{formData.pitch > 0 ? `+${formData.pitch}` : formData.pitch}</span>
                    </div>
                    <input
                      type="range" min={-5} max={5} step={1}
                      value={formData.pitch}
                      onChange={e => onFormDataChange({ pitch: parseInt(e.target.value) })}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>{t('generation:musicEdit.form.pitchDown')}</span>
                      <span>0</span>
                      <span>{t('generation:musicEdit.form.pitchUp')}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">{t('generation:musicEdit.form.pitchHint')}</p>
                  </div>

                  {/* 音色融合度 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-300 font-medium">{t('generation:musicEdit.form.indexRateLabel')}</span>
                      <span className="text-xs font-mono text-primary">{formData.indexRate.toFixed(2)}</span>
                    </div>
                    <input
                      type="range" min={0.3} max={0.8} step={0.01}
                      value={formData.indexRate}
                      onChange={e => onFormDataChange({ indexRate: parseFloat(e.target.value) })}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>{t('generation:musicEdit.form.indexRateOriginal')}</span>
                      <span>{t('generation:musicEdit.form.indexRateModel')}</span>
                    </div>
                  </div>

                  {/* 人声音量 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-300 font-medium">{t('generation:musicEdit.form.vocalGainLabel')}</span>
                      <span className="text-xs font-mono text-primary">{formData.vocalGainDb > 0 ? `+${formData.vocalGainDb}` : formData.vocalGainDb}</span>
                    </div>
                    <input
                      type="range" min={-6} max={6} step={0.5}
                      value={formData.vocalGainDb}
                      onChange={e => onFormDataChange({ vocalGainDb: parseFloat(e.target.value) })}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>{t('generation:musicEdit.form.vocalGainDown')}</span>
                      <span>0</span>
                      <span>{t('generation:musicEdit.form.vocalGainUp')}</span>
                    </div>
                  </div>

                  {/* 伴奏音量 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-300 font-medium">{t('generation:musicEdit.form.instrumentalGainLabel')}</span>
                      <span className="text-xs font-mono text-primary">{formData.instrumentalGainDb > 0 ? `+${formData.instrumentalGainDb}` : formData.instrumentalGainDb}</span>
                    </div>
                    <input
                      type="range" min={-6} max={6} step={0.5}
                      value={formData.instrumentalGainDb}
                      onChange={e => onFormDataChange({ instrumentalGainDb: parseFloat(e.target.value) })}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                      <span>{t('generation:musicEdit.form.vocalGainDown')}</span>
                      <span>0</span>
                      <span>{t('generation:musicEdit.form.vocalGainUp')}</span>
                    </div>
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.cleanAudio}
                      onChange={e => onFormDataChange({ cleanAudio: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="text-xs text-gray-300">{t('generation:musicEdit.form.cleanAudio')}</span>
                      <p className="text-[11px] text-gray-500">{t('generation:musicEdit.form.cleanAudioDesc')}</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.f0Autotune}
                      onChange={e => onFormDataChange({ f0Autotune: e.target.checked })}
                      className="w-4 h-4 accent-primary"
                    />
                    <div>
                      <span className="text-xs text-gray-300">{t('generation:musicEdit.form.f0Autotune')}</span>
                      <p className="text-[11px] text-gray-500">{t('generation:musicEdit.form.f0AutotuneDesc')}</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* 输出格式 */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('generation:musicEdit.form.outputFormat')}</label>
              <select
                value={formData.outputFormat}
                onChange={e => onFormDataChange({ outputFormat: e.target.value })}
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
              >
                {OUTPUT_FORMAT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* separate 专用字段 */}
        {formData.editingType === 'separate' && (
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enableBackingVocalRemoval}
                onChange={e => onFormDataChange({ enableBackingVocalRemoval: e.target.checked })}
                className="w-4 h-4 accent-primary"
              />
              <div>
                <span className="text-xs text-gray-300">{t('generation:musicEdit.form.enableBackingVocalRemoval')}</span>
                <p className="text-[11px] text-gray-500">{t('generation:musicEdit.form.enableBackingVocalRemovalDesc')}</p>
              </div>
            </label>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t('generation:musicEdit.form.outputFormat')}</label>
              <select
                value={formData.outputFormat}
                onChange={e => onFormDataChange({ outputFormat: e.target.value })}
                className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:border-primary cursor-pointer"
              >
                {OUTPUT_FORMAT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {t('generation:musicEdit.form.separateDescription')}
            </p>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
        )}
      </div>

      {/* 提交按钮 + 剧点信息 */}
      <div className="p-6 pt-0 space-y-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
        >
          {submitting ? t('generation:musicEdit.form.submitting') : t('generation:musicEdit.form.submitPrefix') + EDITING_TYPE_LABELS[formData.editingType]}
        </button>
        {!modelsLoading && modelPrice > 0 && (
          <div className="flex items-center justify-between text-xs px-1">
            <div className="flex items-center gap-1 text-gray-500">
              <CurrencyDollarIcon className="w-3.5 h-3.5" />
              <span>{t('generation:musicEdit.form.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">{t('generation:musicEdit.form.balance')}</span>
              <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-red-400'}`}>
                {account?.balance ?? 0}
              </span>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
